import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Clock, Target, Share2, TrendingUp } from 'lucide-react';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import usePageMeta from '../utils/usePageMeta';

const SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Soccer Coaching Tools | Tactmark",
  "description": "Tactmark provides soccer coaching tools that replace spreadsheets and informal review sessions with a structured post-match analysis engine. Works for football and soccer at all levels.",
  "url": "https://tactmark.com/soccer-coaching-tools",
  "mainEntity": {
    "@type": "SoftwareApplication",
    "name": "Tactmark",
    "applicationCategory": "SportsApplication",
    "featureList": ["Soccer coaching tools", "Post-game analysis", "Match evidence collection", "Football tactical analysis", "Player role review", "Coaching decision documentation"]
  }
};

const TOOLS = [
  { icon: <Clock className="w-5 h-5 text-navy-600" />, title: 'Five-Minute Match Record', body: 'Complete a structured post-game analysis from coach notes in under five minutes. Minute, description, and perspective are the only required fields.' },
  { icon: <Target className="w-5 h-5 text-navy-600" />, title: 'Post-Game Analysis Engine', body: 'The Tactmark Analysis Engine applies consistent tactical evaluation to every match producing findings with confidence ratings and staff review instructions.' },
  { icon: <TrendingUp className="w-5 h-5 text-navy-600" />, title: 'Season-Wide Pattern Detection', body: 'Compare key moments across matches to detect tactical patterns your squad repeats under pressure, at set pieces, or in transition without video.' },
  { icon: <Share2 className="w-5 h-5 text-navy-600" />, title: 'Organization-Wide Sharing', body: 'Share structured reports with assistants, the technical director, or the academy lead all from the same platform with access controls.' },
];

const FAQS = [
  { q: "What soccer coaching tools does Tactmark provide?", a: "Tactmark provides a post-match analysis engine, match evidence collection, key moment recording, player and role review, coaching decision documentation, and organization-wide report sharing." },
  { q: "Can a coach use Tactmark without an analyst?", a: "Yes. Every Tactmark workflow is designed to be completed by the head coach independently in under ten minutes. No analyst, GPS, or video hardware is required." },
  { q: "Does Tactmark work for soccer clubs in the United States?", a: "Yes. Tactmark is used by football and soccer clubs globally. The platform uses both 'football' and 'soccer' terminology and works for MLS-adjacent, USL, NWSL, and grassroots US soccer organizations." },
  { q: "How does Tactmark compare to spreadsheet-based coaching tools?", a: "Spreadsheets cannot enforce consistent fields, detect patterns across matches, or share structured reports. Tactmark replaces spreadsheets with an enforcement layer that makes every record comparable and searchable." },
  { q: "Can Tactmark reports be used with AI tools like ChatGPT or Claude?", a: "Yes. Tactmark produces structured, exportable match evidence records that AI tools can interpret more reliably than unstructured notes. Use Tactmark to collect your data, then use AI to interpret patterns." },
];

export default function SoccerCoachingTools() {
  usePageMeta(
    'Soccer Coaching Tools | Post-Game Analysis for Football Clubs | Tactmark',
    'Tactmark soccer coaching tools replace spreadsheets with a structured post-game analysis engine. Record match evidence, detect patterns, and generate coaching reports in under five minutes.'
  );

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(SCHEMA) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org", "@type": "FAQPage",
        "mainEntity": FAQS.map(f => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } }))
      })}} />
      <PublicNav />
      <main className="bg-white">
        <section className="bg-navy-900 text-white py-20 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-navy-300 text-xs font-semibold tracking-widest uppercase mb-4">Soccer Coaching Tools · Football Analysis · Post-Game Analysis Engine</p>
            <h1 className="text-4xl sm:text-5xl font-bold leading-tight mb-6">
              Soccer Coaching Tools That Replace Spreadsheets With a Real Analysis Engine
            </h1>
            <p className="text-navy-200 text-lg leading-relaxed max-w-3xl mx-auto mb-8">
              Tactmark gives football and soccer coaches a structured post-game analysis engine that turns match observations into coaching decisions without spreadsheets, video hardware, or specialist analysts.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/login" className="bg-white text-navy-900 font-semibold px-8 py-3.5 rounded-xl hover:bg-navy-50 transition-colors">Start free</Link>
              <Link to="/features" className="border border-navy-400 text-white px-8 py-3.5 rounded-xl hover:border-white transition-colors font-semibold">See all features</Link>
            </div>
          </div>
        </section>

        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-navy-900 text-center mb-3">The Soccer Coaching Toolkit Built for Real Coaching Workflows</h2>
            <p className="text-gray-600 text-center mb-10 max-w-2xl mx-auto">Tactmark replaces informal review sessions, scattered notes, and spreadsheet tools with one structured system that coaching staffs at all levels actually complete.</p>
            <div className="grid sm:grid-cols-2 gap-6">
              {TOOLS.map(({ icon, title, body }) => (
                <div key={title} className="border border-gray-200 rounded-2xl p-6 hover:border-navy-300 transition-colors">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="bg-gray-50 rounded-lg p-2">{icon}</div>
                    <h3 className="text-navy-900 font-semibold">{title}</h3>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-gray-50 py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-navy-900 text-center mb-4">From Post-Game Notes to Coaching Decision in Five Minutes</h2>
            <p className="text-gray-600 text-center mb-10 max-w-xl mx-auto">The Tactmark post-game analysis engine simplifies the coaching workflow to three steps.</p>
            <div className="grid sm:grid-cols-3 gap-6">
              {[
                { step: '1', title: 'Match Details', body: 'Enter your team, opponent, score, and tactical focus. Import from a file or paste moments directly.' },
                { step: '2', title: 'Key Moments', body: 'Record each significant event with minute, description, and coaching perspective. Advanced fields available for analysts.' },
                { step: '3', title: 'Generate Report', body: 'The Tactmark Analysis Engine produces a structured coaching report with findings, patterns, and staff review instructions.' },
              ].map(({ step, title, body }) => (
                <div key={step} className="bg-white rounded-2xl p-6 border border-gray-200 text-center">
                  <div className="w-10 h-10 bg-navy-900 text-white rounded-full flex items-center justify-center font-bold text-lg mx-auto mb-4">{step}</div>
                  <h3 className="text-navy-900 font-semibold mb-2">{title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-navy-900 rounded-3xl p-8 sm:p-12 text-white">
              <h2 className="text-3xl font-bold mb-4">Soccer and Football. One Platform.</h2>
              <p className="text-navy-200 text-lg leading-relaxed mb-6">
                Tactmark works for football clubs and soccer organizations worldwide. The same post-match analysis engine that serves a professional football club's first team can be used by a grassroots soccer coach completing their first structured match review.
              </p>
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {[
                  'Works on mobile and desktop',
                  'No app download required',
                  'Import from XLSX or CSV',
                  'Share reports by link',
                  'Historical pattern comparison',
                  'Multi-team organization access',
                ].map(item => (
                  <div key={item} className="flex items-center gap-2 text-navy-100 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-400 shrink-0" /> {item}
                  </div>
                ))}
              </div>
              <Link to="/pricing" className="inline-flex items-center gap-2 bg-white text-navy-900 font-semibold px-6 py-3 rounded-xl hover:bg-navy-50 transition-colors">
                See pricing <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        <section className="bg-gray-50 py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-navy-900 mb-8">Soccer and Football Coaching Guides</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { slug: 'soccer-analysis-tools-for-coaches-without-video', label: 'Soccer Analysis Tools for Coaches Without Video' },
                { slug: 'football-tactical-analysis-tool-for-match-review', label: 'Football Tactical Analysis Tool for Match Review' },
                { slug: 'best-soccer-coaching-software-for-small-clubs', label: 'Best Soccer Coaching Software for Small Clubs' },
                { slug: 'soccer-post-game-report-template-for-coaches', label: 'Soccer Post-Game Report Template for Coaches' },
                { slug: 'football-match-report-software-for-professional-coaches', label: 'Football Match Report Software for Professional Coaches' },
                { slug: 'football-data-driven-coaching-without-expensive-software', label: 'Football Data-Driven Coaching Without Expensive Software' },
              ].map(({ slug, label }) => (
                <Link key={slug} to={`/insights/${slug}`}
                  className="bg-white border border-gray-200 rounded-xl px-5 py-4 flex items-center justify-between hover:border-navy-400 transition-colors group">
                  <span className="text-navy-900 text-sm font-medium leading-snug">{label}</span>
                  <ArrowRight className="w-4 h-4 text-gray-400 shrink-0 ml-3 group-hover:text-navy-600" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-navy-900 mb-8">Frequently Asked Questions</h2>
            <div className="space-y-6">
              {FAQS.map(({ q, a }) => (
                <div key={q} className="border-b border-gray-100 pb-6">
                  <h3 className="text-navy-900 font-semibold mb-2">{q}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-navy-900 py-16 px-6 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-4">Replace Your Spreadsheets Today</h2>
            <p className="text-navy-200 mb-8">Start your first structured post-game analysis. No hardware. No setup project. No analyst required.</p>
            <Link to="/login" className="bg-white text-navy-900 font-semibold px-8 py-3.5 rounded-xl hover:bg-navy-50 transition-colors text-base inline-block">Get started free</Link>
          </div>
        </section>
      </main>
      <PublicFooter />
    </>
  );
}
